import React, { useEffect, useRef, useState } from "react";
import { useAuth } from "../lib/auth";
import { useLanguage } from "../lib/language";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { LifeBuoy, ImageIcon, Send, X, Upload, Tag } from "lucide-react";

const compressImage = (file) => new Promise((resolve) => {
    if (file.size < 200 * 1024) {
        const r = new FileReader();
        r.onload = () => resolve(r.result);
        r.readAsDataURL(file);
        return;
    }
    const img = new Image();
    img.onload = () => {
        const canvas = document.createElement("canvas");
        const maxW = 1200;
        const scale = Math.min(1, maxW / img.width);
        canvas.width = img.width * scale;
        canvas.height = img.height * scale;
        canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL("image/jpeg", 0.8));
    };
    img.src = URL.createObjectURL(file);
});

export default function Support() {
    const { user } = useAuth();
    const { t } = useLanguage();
    const nav = useNavigate();
    const [params] = useSearchParams();
    const [subject, setSubject] = useState("");
    const [message, setMessage] = useState("");
    const [tickets, setTickets] = useState([]);
    const [openTid, setOpenTid] = useState(params.get("tid") || null);
    const [openTicket, setOpenTicket] = useState(null);
    const [reply, setReply] = useState("");
    const [replyImgs, setReplyImgs] = useState([]);
    const fileRef = useRef(null);

    const loadTickets = async () => {
        if (!user) return;
        const { data } = await api.get("/tickets/mine");
        setTickets(data);
    };
    useEffect(() => { loadTickets(); /* eslint-disable-next-line */ }, [user]);
    useEffect(() => {
        if (openTid) api.get(`/tickets/${openTid}`).then(({ data }) => setOpenTicket(data)).catch(() => {});
    }, [openTid]);

    // Auto-refresh open ticket every 5s so new admin replies appear without reloading the page
    useEffect(() => {
        if (!openTid) return;
        const iv = setInterval(async () => {
            try {
                const { data } = await api.get(`/tickets/${openTid}`);
                setOpenTicket((prev) => {
                    // Only update if message count or status changed (avoid disrupting scroll)
                    if (!prev || (data.messages || []).length !== (prev.messages || []).length || data.status !== prev.status) {
                        return data;
                    }
                    return prev;
                });
            } catch (err) { console.warn("[tickets-polling] transient error:", err?.message || err); }
        }, 5000);
        return () => clearInterval(iv);
    }, [openTid]);

    // Refresh ticket list every 15s while user is on the page
    useEffect(() => {
        if (!user) return;
        const iv = setInterval(() => { loadTickets().catch(() => {}); }, 15000);
        return () => clearInterval(iv);
        // eslint-disable-next-line
    }, [user]);

    const submit = async (e) => {
        e.preventDefault();
        if (!user) { nav("/login?next=/soporte"); return; }
        try {
            const { data } = await api.post("/tickets", { subject, message });
            toast.success(t("Ticket creado: ", "Ticket created: ") + data._id);
            setSubject(""); setMessage("");
            await loadTickets();
            setOpenTid(data._id);
        } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    };

    const onPickImgs = async (files) => {
        const arr = Array.from(files).slice(0, 4 - replyImgs.length);
        const dataUrls = await Promise.all(arr.map(compressImage));
        setReplyImgs([...replyImgs, ...dataUrls].slice(0, 4));
    };

    const sendReply = async () => {
        if (!reply.trim() && replyImgs.length === 0) return;
        try {
            const { data } = await api.post(`/tickets/${openTid}/reply`, { message: reply, attachments: replyImgs });
            setOpenTicket(data);
            setReply(""); setReplyImgs([]);
            await loadTickets();
        } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    };

    // Detail view of a single ticket (chat)
    if (openTid && openTicket) {
        return (
            <div className="max-w-3xl mx-auto px-6 py-10 fade-in">
                <button onClick={() => { setOpenTid(null); setOpenTicket(null); nav("/soporte"); }} className="tb-link text-sm mb-4" data-testid="back-tickets">← {t("Volver a tickets", "Back to tickets")}</button>
                <div className="tb-card p-5 mb-4">
                    <div className="flex justify-between items-start gap-3 flex-wrap">
                        <div>
                            <div className="font-mono text-xs text-[var(--tb-muted)]">{openTicket._id}</div>
                            <div className="font-display text-2xl">{openTicket.subject}</div>
                            {openTicket.tag === "product_request" && (
                                <div className="text-xs text-[var(--tb-gold)] mt-1 inline-flex items-center gap-1"><Tag className="w-3 h-3" /> {t("Solicitud de producto", "Product request")}</div>
                            )}
                        </div>
                        <span className="tb-badge tb-badge-gold">{openTicket.status}</span>
                    </div>
                </div>

                {/* Messages */}
                <div className="space-y-3 mb-4">
                    {openTicket.messages.map((m, i) => (
                        <div key={`m-${i}-${m.at || ""}`} className={`flex ${m.from === "admin" ? "justify-start" : "justify-end"}`}>
                            <div className={`max-w-[75%] p-3 rounded-lg ${m.from === "admin" ? "bg-[var(--tb-bg-2)]/50 border border-[var(--tb-border)]" : "bg-[var(--tb-border-gold)]/20 border border-[var(--tb-gold)]/30"}`}>
                                <div className="text-[10px] text-[var(--tb-muted)] mb-1 capitalize">{m.from === "admin" ? "Terra Boutique" : t("Tú", "You")} · {m.at ? new Date(m.at).toLocaleString(t("es-SV", "en-US")) : ""}</div>
                                {m.text && <div className="text-sm whitespace-pre-wrap">{m.text}</div>}
                                {(m.attachments || []).length > 0 && (
                                    <div className="flex gap-1.5 mt-2 flex-wrap">
                                        {m.attachments.map((src, k) => (
                                            <a key={`att-${i}-${k}`} href={src} target="_blank" rel="noreferrer">
                                                <img src={src} alt="adjunto" className="w-24 h-24 object-cover rounded border border-[var(--tb-border)]" />
                                            </a>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>

                {/* Reply box */}
                {openTicket.status !== "closed" ? (
                    <div className="tb-card p-4 space-y-3" data-testid="reply-box">
                        {replyImgs.length > 0 && (
                            <div className="flex gap-2 flex-wrap">
                                {replyImgs.map((src, i) => (
                                    <div key={`pre-${i}`} className="relative">
                                        <img src={src} alt="" className="w-16 h-16 object-cover rounded border border-[var(--tb-border)]" />
                                        <button onClick={() => setReplyImgs(replyImgs.filter((_, j) => j !== i))} className="absolute -top-1.5 -right-1.5 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center"><X className="w-3 h-3" /></button>
                                    </div>
                                ))}
                            </div>
                        )}
                        <textarea className="tb-input min-h-[80px]" placeholder={t("Escribe tu mensaje...", "Write your message...")} value={reply} onChange={(e) => setReply(e.target.value)} data-testid="reply-text" />
                        <div className="flex justify-between items-center">
                            <button type="button" onClick={() => fileRef.current?.click()} className="tb-link text-xs inline-flex items-center gap-1" disabled={replyImgs.length >= 4}>
                                <Upload className="w-3.5 h-3.5" /> {t("Adjuntar imagen", "Attach image")} ({replyImgs.length}/4)
                            </button>
                            <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => onPickImgs(e.target.files)} data-testid="reply-files" />
                            <button onClick={sendReply} className="tb-btn-primary inline-flex items-center gap-2" data-testid="reply-send">
                                <Send className="w-4 h-4" /> {t("Enviar", "Send")}
                            </button>
                        </div>
                    </div>
                ) : (
                    <div className="tb-card p-4 text-center text-sm text-[var(--tb-text-2)]">{t("Ticket cerrado", "Ticket closed")}</div>
                )}
            </div>
        );
    }

    return (
        <div className="max-w-4xl mx-auto px-6 py-12 fade-in">
            <LifeBuoy className="w-10 h-10 text-[var(--tb-gold)] mb-4" />
            <h1 className="font-display text-4xl mb-2">{t("Soporte Terra", "Terra Support")}</h1>
            <p className="text-[var(--tb-text-2)] mb-8">{t("¿Necesitas ayuda? Abre un ticket y te responderemos en menos de 24 horas.", "Need help? Open a ticket and we'll reply within 24 hours.")}</p>

            <form onSubmit={submit} className="tb-card p-6 space-y-4 mb-10">
                <input className="tb-input" placeholder={t("Asunto", "Subject")} value={subject} onChange={(e) => setSubject(e.target.value)} required data-testid="ticket-subject" />
                <textarea className="tb-input min-h-[120px]" placeholder={t("Describe tu consulta...", "Describe your inquiry...")} value={message} onChange={(e) => setMessage(e.target.value)} required data-testid="ticket-message" />
                <button type="submit" className="tb-btn-primary" data-testid="ticket-submit">
                    {user ? t("Enviar ticket", "Submit ticket") : t("Acceder para enviar", "Sign in to submit")}
                </button>
            </form>

            {user && tickets.length > 0 && (
                <div>
                    <h2 className="font-display text-2xl mb-4">{t("Mis tickets", "My tickets")}</h2>
                    <div className="space-y-3">
                        {tickets.map((tk) => (
                            <button key={tk._id} onClick={() => { setOpenTid(tk._id); nav(`/soporte?tid=${tk._id}`); }} className="tb-card p-4 w-full text-left hover:border-[var(--tb-gold)] transition" data-testid={`ticket-${tk._id}`}>
                                <div className="flex justify-between gap-3 flex-wrap">
                                    <div className="flex-1 min-w-0">
                                        <div className="font-mono text-xs text-[var(--tb-muted)]">{tk._id}</div>
                                        <div className="font-medium truncate">{tk.subject}</div>
                                        {tk.tag === "product_request" && (
                                            <div className="text-[10px] text-[var(--tb-gold)] mt-1 inline-flex items-center gap-1"><Tag className="w-2.5 h-2.5" /> {t("Solicitud de producto", "Product request")}</div>
                                        )}
                                    </div>
                                    <div className="text-right">
                                        <span className="tb-badge tb-badge-gold">{tk.status}</span>
                                        <div className="text-[10px] text-[var(--tb-muted)] mt-1">{tk.messages.length} {tk.messages.length === 1 ? t("mensaje", "message") : t("mensajes", "messages")}</div>
                                    </div>
                                </div>
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
